import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PublicPlan, AIGeneratedContent } from '@data/publicPlans';
import type { GanttTask } from '../../types/gantt';
import {
	applyWorkdayTaskUpdate,
	ganttTasksFromPlanInstructions,
	ganttTasksFromStructuredTimeline,
	generateSyntheticGanttData,
	moveTimelineTaskInOrder,
	normalizeCompanyCalendar,
	scheduleTimelineTasksSequentially,
	syncAiContentFromTimeline,
	type CompanyCalendar
} from '@utils/ganttDataGenerator';
import { getCompanySchedule } from '@services/companies';

type Language = 'en' | 'de' | 'hr';

export function buildTimelineTasksFromPlan(
	plan: PublicPlan | null,
	content: AIGeneratedContent | null,
	language: Language
): GanttTask[] {
	if (!plan?.aiGenerated || !content) return [];
	const instructions = content.instructions ?? plan.aiGenerated.instructions ?? [];
	const structuredTimelineTasks = content.timeline_tasks ?? (plan.aiGenerated as AIGeneratedContent).timeline_tasks ?? [];
	const startDate = plan.questionnaire?.projectStartDate
		? new Date(plan.questionnaire.projectStartDate)
		: plan.createdAt
			? new Date(plan.createdAt)
			: new Date();

	const structured = ganttTasksFromStructuredTimeline(structuredTimelineTasks, startDate);
	if (structured.length > 0) return structured;
	if (!instructions.length) return [];

	const planBased = ganttTasksFromPlanInstructions(instructions, startDate);
	if (planBased.length > 0) return planBased;

	const budgetMid = content.pricingBreakdown?.mid ?? plan.aiGenerated.pricingBreakdown?.mid ?? 0;
	return generateSyntheticGanttData('garden', budgetMid, startDate, undefined, undefined, language);
}

export function usePlanTimelineTasks(
	plan: PublicPlan | null,
	content: AIGeneratedContent | null,
	onContentChange: (next: AIGeneratedContent) => void,
	language: Language,
	options?: { loadCompanySchedule?: boolean; companyUserId?: number | null }
) {
	const projectStartDate = useMemo(() => {
		if (plan?.questionnaire?.projectStartDate) {
			return plan.questionnaire.projectStartDate.split('T')[0];
		}
		if (plan?.createdAt) {
			return new Date(plan.createdAt).toISOString().split('T')[0];
		}
		return new Date().toISOString().split('T')[0];
	}, [plan?.questionnaire?.projectStartDate, plan?.createdAt]);

	const [timelineTasks, setTimelineTasks] = useState<GanttTask[]>([]);
	const [companyCalendar, setCompanyCalendar] = useState<CompanyCalendar | null>(null);
	const planKey = plan?.databaseId ?? plan?.id ?? '';

	useEffect(() => {
		if (!content || !plan) {
			setTimelineTasks([]);
			return;
		}
		setTimelineTasks(buildTimelineTasksFromPlan(plan, content, language));
	}, [planKey, language]);

	useEffect(() => {
		if (!options?.loadCompanySchedule || !options.companyUserId) return;
		let cancelled = false;
		getCompanySchedule(String(options.companyUserId))
			.then((s) => {
				if (!cancelled) {
					setCompanyCalendar(
						normalizeCompanyCalendar({
							workingDays: s.working_days ?? null,
							nonWorkingDates: Array.isArray(s.non_working_dates) ? s.non_working_dates : []
						})
					);
				}
			})
			.catch(() => {
				if (!cancelled) setCompanyCalendar(null);
			});
		return () => {
			cancelled = true;
		};
	}, [options?.loadCompanySchedule, options?.companyUserId]);

	const commitTasks = useCallback(
		(nextTasks: GanttTask[]) => {
			setTimelineTasks(nextTasks);
			if (!content) return;
			const synced = syncAiContentFromTimeline(content, nextTasks, language);
			onContentChange({ ...content, ...synced } as AIGeneratedContent);
		},
		[content, onContentChange, language]
	);

	const handleTaskUpdate = useCallback(
		(taskId: string, updates: Partial<GanttTask>) => {
			if (!projectStartDate) return;
			const next = applyWorkdayTaskUpdate(
				timelineTasks,
				taskId,
				updates,
				new Date(projectStartDate),
				companyCalendar
			);
			commitTasks(next);
		},
		[timelineTasks, projectStartDate, companyCalendar, commitTasks]
	);

	const moveTask = useCallback(
		(sortedIndex: number, direction: 'up' | 'down') => {
			if (!projectStartDate) return;
			const reordered = moveTimelineTaskInOrder(
				[...timelineTasks],
				sortedIndex,
				direction,
				new Date(projectStartDate),
				companyCalendar ?? undefined
			);
			commitTasks(reordered);
		},
		[timelineTasks, projectStartDate, companyCalendar, commitTasks]
	);

	const rescheduleSequential = useCallback(() => {
		if (!projectStartDate || timelineTasks.length === 0) return;
		const next = scheduleTimelineTasksSequentially(
			timelineTasks,
			new Date(projectStartDate),
			companyCalendar
		);
		commitTasks(next);
	}, [timelineTasks, projectStartDate, companyCalendar, commitTasks]);

	return {
		timelineTasks,
		projectStartDate,
		companyCalendar,
		commitTasks,
		handleTaskUpdate,
		moveTask,
		rescheduleSequential
	};
}
